// ── GroupBuyingPage — 공동구매 마켓플레이스 ──
// 셈핏 운영자가 큐레이션한 공동구매 카드를 모든 사용자에게 노출.
// 사용자는 관심/참여 의향을 등록하면 운영자가 카톡으로 후속 진행.

import React, { useState, useEffect, useMemo } from 'react';
import {
  collection, onSnapshot, query, orderBy,
  addDoc, doc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../db';
import {
  GB_STATUS, GB_CATEGORIES, PARTICIPANT_STATUS,
  emptyGroupBuying, emptyParticipant,
  calcSavings, daysUntilDeadline, effectiveStatus,
  sortGroupBuyings, aggregateParticipants, findMyParticipation,
  formatKRW,
} from '../utils/groupBuying';
import { subscribeSuppliers } from '../utils/suppliers';
import SupplierDirectory from './SupplierDirectory';

export default function GroupBuyingPage({ user, isAdminUser }) {
  const [groupBuyings, setGroupBuyings] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');  // all/야채/육류/...
  const [showAdmin, setShowAdmin] = useState(false);
  const [editingGb, setEditingGb] = useState(null);  // 운영자 등록/수정 모달
  const [selectedGb, setSelectedGb] = useState(null);  // 사용자 상세/참여 모달
  const [suppliers, setSuppliers] = useState([]);       // V18: 공급처 마스터
  const [adminView, setAdminView] = useState('deals'); // V18: 'deals' | 'suppliers' (admin 전용)

  // V18: 공급처 구독
  useEffect(() => {
    const unsub = subscribeSuppliers(
      (list) => setSuppliers(list),
      (err) => console.warn('suppliers load:', err.message)
    );
    return unsub;
  }, []);

  // 공동구매 목록 구독
  useEffect(() => {
    const q = query(collection(db, 'groupBuyings'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setGroupBuyings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.warn('공동구매 조회 실패:', err.message);
      setLoading(false);
    });
    return unsub;
  }, []);

  // 참여자 목록 구독 (운영자는 전체, 사용자는 본인 것만)
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, 'groupBuyingParticipants'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // 운영자: 전체 / 일반 사용자: 본인 것만 (보안 규칙에서도 enforce 권장)
      setParticipants(isAdminUser ? all : all.filter(p => p.userId === user.uid));
    }, (err) => {
      console.warn('참여자 조회 실패:', err.message);
    });
    return unsub;
  }, [user?.uid, isAdminUser]);

  // V18: 공급처 ID → 객체 lookup (카드/모달 표시용)
  const supplierById = useMemo(() => {
    const m = new Map();
    (suppliers || []).forEach(sp => m.set(sp.id, sp));
    return m;
  }, [suppliers]);

  // 카테고리 필터링 + 정렬
  const visible = useMemo(() => {
    let list = groupBuyings;
    if (filter !== 'all') list = list.filter(gb => gb.category === filter);
    return sortGroupBuyings(list);
  }, [groupBuyings, filter]);

  const openParticipateModal = (gb) => setSelectedGb(gb);

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#f0f2f5' }}>
      {/* 헤더 */}
      <div style={{
        padding: '20px 20px 14px', background: '#fff',
        borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 20,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>
              🛒 공동구매
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              셈핏이 큐레이션한 식자재 공동구매 — 더 저렴하게 함께 사세요
            </p>
          </div>
          {isAdminUser && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => setAdminView(adminView === 'suppliers' ? 'deals' : 'suppliers')}
                style={{
                  padding: '8px 14px',
                  background: adminView === 'suppliers' ? '#FF6F0F' : '#fff',
                  color: adminView === 'suppliers' ? '#fff' : '#FF6F0F',
                  border: '1px solid #FF6F0F', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                🏪 공급처 {suppliers.length > 0 ? `(${suppliers.length})` : ''}
              </button>
              {adminView === 'deals' && (
                <button
                  onClick={() => setEditingGb(emptyGroupBuying())}
                  style={{
                    padding: '8px 14px', background: '#FF6F0F', color: '#fff',
                    border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  + 공동구매 등록
                </button>
              )}
            </div>
          )}
        </div>

        {/* 카테고리 필터 (deals 뷰일 때만) */}
        {adminView === 'deals' && (
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
            <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>전체</FilterChip>
            {GB_CATEGORIES.map(c => (
              <FilterChip
                key={c.value}
                active={filter === c.value}
                onClick={() => setFilter(c.value)}
              >
                {c.label}
              </FilterChip>
            ))}
          </div>
        )}
      </div>

      {/* V18: admin 공급처 뷰 또는 일반 deals 리스트 */}
      {isAdminUser && adminView === 'suppliers' ? (
        <SupplierDirectory user={user} isAdminUser={isAdminUser} />
      ) : (
      /* 카드 리스트 */
      <div style={{ padding: 20 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>불러오는 중...</div>
        ) : visible.length === 0 ? (
          <EmptyState isAdminUser={isAdminUser} onCreate={() => setEditingGb(emptyGroupBuying())} />
        ) : (
          <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {visible.map(gb => {
              const agg = aggregateParticipants(participants, gb.id);
              const mine = findMyParticipation(participants, gb.id, user?.uid);
              return (
                <GroupBuyingCard
                  key={gb.id}
                  gb={gb}
                  agg={agg}
                  myParticipation={mine}
                  isAdminUser={isAdminUser}
                  onClick={() => openParticipateModal(gb)}
                  onEdit={() => setEditingGb(gb)}
                />
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* 사용자 참여 모달 */}
      {selectedGb && (
        <ParticipateModal
          gb={selectedGb}
          user={user}
          participants={participants}
          isAdminUser={isAdminUser}
          suppliers={suppliers}
          onClose={() => setSelectedGb(null)}
        />
      )}

      {/* 운영자 등록/수정 모달 */}
      {editingGb && (
        <AdminEditModal
          gb={editingGb}
          user={user}
          suppliers={suppliers}
          onClose={() => setEditingGb(null)}
        />
      )}
    </div>
  );
}

// ───────────────────────── 카드 ─────────────────────────

function GroupBuyingCard({ gb, agg, myParticipation, isAdminUser, onClick, onEdit }) {
  const sav = calcSavings(gb);
  const days = daysUntilDeadline(gb.deadline);
  const eff = effectiveStatus(gb);
  const status = GB_STATUS[eff] || GB_STATUS.open;

  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', borderRadius: 14, padding: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', cursor: 'pointer',
        border: myParticipation ? '2px solid #FF6F0F' : '1px solid #e5e7eb',
        transition: 'all 0.15s ease', position: 'relative',
      }}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
    >
      {/* 상태 배지 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <span style={{
          padding: '3px 8px', borderRadius: 6,
          background: status.bg, color: status.color,
          fontSize: 11, fontWeight: 600,
        }}>
          {status.label}
          {eff === 'closing' && days !== null && ` · D-${days}`}
          {eff === 'open' && days !== null && days > 3 && ` · D-${days}`}
        </span>
        {isAdminUser && (
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            style={{
              padding: '2px 8px', fontSize: 11, background: '#f3f4f6',
              border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer',
            }}
          >
            수정
          </button>
        )}
      </div>

      {/* 상품 정보 */}
      <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#111827' }}>
        {gb.title || gb.productName || '제목 없음'}
      </h3>
      <p style={{ margin: '0 0 10px', fontSize: 12, color: '#6b7280' }}>
        {gb.productName && gb.title ? `${gb.productName} · ` : ''}{gb.unit}
      </p>

      {/* 가격 */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: '#18181B' }}>
          ₩{formatKRW(gb.pricePerUnit)}
        </span>
        {gb.marketPrice > 0 && (
          <span style={{ fontSize: 12, color: '#9ca3af', textDecoration: 'line-through' }}>
            ₩{formatKRW(gb.marketPrice)}
          </span>
        )}
      </div>

      {/* 절감 */}
      {sav.amount > 0 && (
        <div style={{
          padding: '6px 10px', background: '#F4F4F5', borderRadius: 6,
          fontSize: 12, color: '#52525B', fontWeight: 600, marginBottom: 10,
        }}>
          💰 시세 대비 {sav.percent}% 절감 · ₩{formatKRW(sav.amount)}/단위
        </div>
      )}

      {/* 참여자 + 픽업 */}
      <div style={{ fontSize: 12, color: '#6b7280', display: 'grid', gap: 4 }}>
        <div>👥 {agg.userCount}명 참여 · 총 {agg.totalQuantity}{gb.unit ? `${gb.unit}` : '개'}</div>
        {gb.pickupDate && <div>📅 {gb.pickupDate} {gb.pickupLocation}</div>}
        {gb.supplier && <div>🏬 {gb.supplier}</div>}
      </div>

      {/* 본인 참여 표시 */}
      {myParticipation && (
        <div style={{
          marginTop: 10, padding: '6px 10px', background: "#F4F4F5",
          borderRadius: 6, fontSize: 12, color: '#9A3412', fontWeight: 600,
        }}>
          ✅ 참여 중 · {myParticipation.quantity}{gb.unit ? `${gb.unit}` : '개'}
        </div>
      )}
    </div>
  );
}

// ───────────────────────── 필터 칩 ─────────────────────────

function FilterChip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 12px', borderRadius: 999,
        background: active ? '#FF6F0F' : '#fff',
        color: active ? '#fff' : '#374151',
        border: active ? 'none' : '1px solid #e5e7eb',
        fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
        cursor: 'pointer', flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

// ───────────────────────── 빈 상태 ─────────────────────────

function EmptyState({ isAdminUser, onCreate }) {
  return (
    <div style={{
      padding: 40, textAlign: 'center', background: '#fff',
      borderRadius: 14, border: '2px dashed #e5e7eb',
    }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🛒</div>
      <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#111827' }}>
        현재 진행 중인 공동구매가 없습니다
      </h3>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>
        {isAdminUser
          ? '운영자 — 새 공동구매를 등록해보세요'
          : '곧 새로운 공동구매가 올라올 예정입니다'}
      </p>
      {isAdminUser && (
        <button
          onClick={onCreate}
          style={{
            padding: '10px 18px', background: '#FF6F0F', color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + 첫 공동구매 등록
        </button>
      )}
    </div>
  );
}

// ───────────────────────── 사용자 참여 모달 ─────────────────────────

function ParticipateModal({ gb, user, participants, isAdminUser, suppliers, onClose }) {
  const existing = findMyParticipation(participants, gb.id, user?.uid);
  const [quantity, setQuantity] = useState(existing?.quantity || 1);
  const [phone, setPhone] = useState(existing?.phone || '');
  const [memo, setMemo] = useState(existing?.memo || '');
  const [saving, setSaving] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);

  const agg = aggregateParticipants(participants, gb.id);
  const sav = calcSavings(gb);
  const eff = effectiveStatus(gb);
  const closed = eff === 'closed' || eff === 'fulfilled';

  const handleSubmit = async () => {
    if (!user?.uid) { alert('로그인이 필요합니다'); return; }
    if (closed) { alert('이미 마감된 공동구매입니다'); return; }
    if (!phone.trim()) { alert('연락처를 입력해주세요'); return; }
    if (quantity < 1) { alert('수량을 1 이상으로 입력해주세요'); return; }

    setSaving(true);
    try {
      if (existing) {
        await updateDoc(doc(db, 'groupBuyingParticipants', existing.id), {
          quantity: Number(quantity),
          phone: phone.trim(),
          memo: memo.trim(),
          status: 'interested',
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, 'groupBuyingParticipants'), {
          ...emptyParticipant(gb.id, user),
          quantity: Number(quantity),
          phone: phone.trim(),
          memo: memo.trim(),
          createdAt: serverTimestamp(),
        });
      }
      alert('참여 신청 완료! 운영자가 곧 연락드릴게요.');
      onClose();
    } catch (e) {
      alert(`신청 실패: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!existing) return;
    if (!window.confirm('참여를 취소할까요?')) return;
    try {
      await updateDoc(doc(db, 'groupBuyingParticipants', existing.id), {
        status: 'cancelled',
        cancelledAt: serverTimestamp(),
      });
      alert('참여를 취소했습니다');
      onClose();
    } catch (e) {
      alert(`취소 실패: ${e.message}`);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700 }}>
        {gb.title || gb.productName}
      </h3>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
        {gb.productName} · {gb.unit}
      </p>

      {/* 가격 & 절감 */}
      <div style={{
        padding: 14, background: '#f9fafb', borderRadius: 10, marginBottom: 14,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>공동구매가</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#18181B' }}>
            ₩{formatKRW(gb.pricePerUnit)}
          </span>
        </div>
        {gb.marketPrice > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>시장가</span>
            <span style={{ fontSize: 13, color: '#9ca3af', textDecoration: 'line-through' }}>
              ₩{formatKRW(gb.marketPrice)}
            </span>
          </div>
        )}
        {sav.amount > 0 && (
          <div style={{
            marginTop: 8, padding: '6px 10px', background: '#F4F4F5',
            borderRadius: 6, fontSize: 13, color: '#52525B', fontWeight: 600,
          }}>
            💰 {sav.percent}% 절감 (단위당 ₩{formatKRW(sav.amount)})
          </div>
        )}
      </div>

      {/* 상세 정보 */}
      <div style={{ display: 'grid', gap: 6, fontSize: 13, color: '#374151', marginBottom: 14 }}>
        {gb.deadline && <Row label="📅 모집 마감" value={gb.deadline} />}
        {gb.pickupDate && <Row label="🚚 배송/픽업" value={`${gb.pickupDate} ${gb.pickupLocation || ''}`} />}
        {gb.supplier && !gb.supplierId && <Row label="🏬 공급처" value={gb.supplier} />}
        <Row label="👥 현재 참여" value={`${agg.userCount}명 / 총 ${agg.totalQuantity}${gb.unit || '개'}`} />
        {gb.minOrder > 0 && <Row label="📦 최소 모집" value={`${gb.minOrder}${gb.unit || '개'}`} />}
      </div>

      {/* V20: 공급처 상세 카드 (supplierId 연결된 경우) */}
      <SupplierDetailCard gb={gb} suppliers={suppliers} />

      {gb.description && (
        <div style={{
          padding: 10, background: '#FAFAFA', borderRadius: 8,
          fontSize: 12, color: '#78350f', marginBottom: 14,
          whiteSpace: 'pre-wrap',
        }}>
          📝 {gb.description}
        </div>
      )}

      {/* 참여 폼 */}
      {!closed && (
        <>
          <h4 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: '#111827' }}>
            {existing ? '참여 정보 수정' : '참여 신청'}
          </h4>

          <Field label={`수량 (${gb.unit || '개'})`}>
            <input
              type="number" min="1" value={quantity}
              onChange={e => setQuantity(e.target.value)}
              style={inputStyle}
            />
          </Field>

          <Field label="연락처 (카톡 가능)">
            <input
              type="tel" value={phone} placeholder="010-0000-0000"
              onChange={e => setPhone(e.target.value)}
              style={inputStyle}
            />
          </Field>

          <Field label="메모 (선택)">
            <textarea
              value={memo} placeholder="픽업 시간 요청 등"
              onChange={e => setMemo(e.target.value)}
              rows={2} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </Field>

          <div style={{
            padding: 10, background: '#dbeafe', borderRadius: 8,
            fontSize: 12, color: '#1e40af', marginTop: 10,
          }}>
            💬 신청 후 운영자가 카톡 또는 문자로 결제·배송 안내드립니다.
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            {existing && (
              <button
                onClick={handleCancel}
                style={{
                  flex: 1, padding: '10px', background: '#F4F4F5', color: '#b91c1c',
                  border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                참여 취소
              </button>
            )}
            <button
              onClick={handleSubmit}
              disabled={saving}
              style={{
                flex: 2, padding: '10px', background: '#FF6F0F', color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
                cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? '저장 중...' : existing ? '수정 저장' : '참여 신청'}
            </button>
          </div>
        </>
      )}

      {closed && (
        <div style={{
          padding: 14, background: '#f3f4f6', borderRadius: 8,
          textAlign: 'center', color: '#6b7280', fontSize: 13,
        }}>
          이 공동구매는 마감되었습니다
        </div>
      )}

      {/* 운영자: 참여자 명단 */}
      {isAdminUser && (
        <div style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
          <button
            onClick={() => setShowParticipants(s => !s)}
            style={{
              padding: '6px 10px', background: '#f3f4f6', border: '1px solid #e5e7eb',
              borderRadius: 6, fontSize: 12, cursor: 'pointer',
            }}
          >
            {showParticipants ? '명단 숨기기' : `📋 참여자 명단 (${agg.userCount}명)`}
          </button>
          {showParticipants && (
            <div style={{ marginTop: 10, maxHeight: 200, overflowY: 'auto' }}>
              {agg.list.map(p => (
                <div key={p.id} style={{
                  padding: '8px 10px', borderBottom: '1px solid #f3f4f6',
                  fontSize: 12, display: 'flex', justifyContent: 'space-between',
                }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{p.userName}</div>
                    <div style={{ color: '#6b7280' }}>{p.phone}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div>{p.quantity}{gb.unit || '개'}</div>
                    {p.memo && <div style={{ color: '#6b7280', fontSize: 11 }}>{p.memo}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ───────────────────────── 운영자 등록/수정 모달 ─────────────────────────

function AdminEditModal({ gb, user, suppliers, onClose }) {
  const [form, setForm] = useState(gb);
  const [saving, setSaving] = useState(false);
  const isNew = !gb.id;

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title && !form.productName) { alert('제목 또는 상품명을 입력해주세요'); return; }
    if (!form.pricePerUnit) { alert('공동구매가를 입력해주세요'); return; }
    setSaving(true);
    try {
      const data = {
        ...form,
        pricePerUnit: Number(form.pricePerUnit) || 0,
        marketPrice: Number(form.marketPrice) || 0,
        minOrder: Number(form.minOrder) || 1,
        maxOrder: form.maxOrder ? Number(form.maxOrder) : null,
        updatedAt: serverTimestamp(),
      };
      if (isNew) {
        await addDoc(collection(db, 'groupBuyings'), {
          ...data,
          createdAt: serverTimestamp(),
          createdBy: user?.uid || '',
        });
      } else {
        const { id, ...rest } = data;
        await updateDoc(doc(db, 'groupBuyings', id), rest);
      }
      onClose();
    } catch (e) {
      alert(`저장 실패: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!gb.id) return;
    if (!window.confirm('이 공동구매를 삭제할까요? (참여자 명단은 유지됩니다)')) return;
    try {
      await deleteDoc(doc(db, 'groupBuyings', gb.id));
      onClose();
    } catch (e) {
      alert(`삭제 실패: ${e.message}`);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h3 style={{ margin: '0 0 14px', fontSize: 18, fontWeight: 700 }}>
        {isNew ? '공동구매 등록' : '공동구매 수정'}
      </h3>

      <Field label="제목 (사용자에게 보이는 헤드라인)">
        <input
          value={form.title}
          onChange={e => update('title', e.target.value)}
          placeholder="예) 남해 지역 농가 직거래 — 양배추 박스"
          style={inputStyle}
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
        <Field label="상품명">
          <input
            value={form.productName}
            onChange={e => update('productName', e.target.value)}
            placeholder="양배추"
            style={inputStyle}
          />
        </Field>
        <Field label="단위">
          <input
            value={form.unit}
            onChange={e => update('unit', e.target.value)}
            placeholder="10kg / 1박스"
            style={inputStyle}
          />
        </Field>
      </div>

      <Field label="카테고리">
        <select
          value={form.category}
          onChange={e => update('category', e.target.value)}
          style={inputStyle}
        >
          <option value="">선택</option>
          {GB_CATEGORIES.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Field label="공동구매가 (원)">
          <input
            type="number" value={form.pricePerUnit}
            onChange={e => update('pricePerUnit', e.target.value)}
            placeholder="15000" style={inputStyle}
          />
        </Field>
        <Field label="시장가 (원, 선택)">
          <input
            type="number" value={form.marketPrice}
            onChange={e => update('marketPrice', e.target.value)}
            placeholder="22000" style={inputStyle}
          />
        </Field>
      </div>

      <Field label="공급처">
        <select
          value={form.supplierId || ''}
          onChange={e => {
            const id = e.target.value;
            const sup = (suppliers || []).find(x => x.id === id);
            update('supplierId', id);
            update('supplierName', sup ? sup.name : '');
            update('supplier', sup ? sup.name : (form.supplier || ''));
          }}
          style={inputStyle}>
          <option value="">(공급처 선택 안 함)</option>
          {(suppliers || []).filter(sp => sp.status === 'active').map(sp => (
            <option key={sp.id} value={sp.id}>
              {sp.name}{sp.region ? ` · ${sp.region}` : ''}
            </option>
          ))}
        </select>
        <input
          value={form.supplier || ''}
          onChange={e => update('supplier', e.target.value)}
          placeholder="공급처 직접 입력 (선택 안 한 경우)"
          style={{ ...inputStyle, marginTop: 6 }}
        />
        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
          💡 등록된 공급처는 위 드롭다운에서, 없으면 아래 직접 입력. "🏪 공급처" 버튼으로 마스터 관리.
        </div>
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Field label="최소 모집 수량">
          <input
            type="number" value={form.minOrder}
            onChange={e => update('minOrder', e.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="최대 (선택)">
          <input
            type="number" value={form.maxOrder || ''}
            onChange={e => update('maxOrder', e.target.value)}
            placeholder="무제한" style={inputStyle}
          />
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Field label="모집 마감일">
          <input
            type="date" value={form.deadline}
            onChange={e => update('deadline', e.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="배송/픽업 예정일">
          <input
            type="date" value={form.pickupDate}
            onChange={e => update('pickupDate', e.target.value)}
            style={inputStyle}
          />
        </Field>
      </div>

      <Field label="픽업 위치">
        <input
          value={form.pickupLocation}
          onChange={e => update('pickupLocation', e.target.value)}
          placeholder="남해읍 OO마트 주차장 / 택배 배송"
          style={inputStyle}
        />
      </Field>

      <Field label="상세 설명 (선택)">
        <textarea
          value={form.description}
          onChange={e => update('description', e.target.value)}
          rows={3}
          placeholder="결제 방법, 픽업 시간, 주의사항 등"
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </Field>

      <Field label="상태">
        <select
          value={form.status}
          onChange={e => update('status', e.target.value)}
          style={inputStyle}
        >
          <option value="open">모집 중</option>
          <option value="closed">마감</option>
          <option value="fulfilled">배송 완료</option>
        </select>
      </Field>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        {!isNew && (
          <button
            onClick={handleDelete}
            style={{
              padding: '10px 14px', background: '#F4F4F5', color: '#b91c1c',
              border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            삭제
          </button>
        )}
        <button
          onClick={onClose}
          style={{
            flex: 1, padding: '10px', background: '#f3f4f6', color: '#374151',
            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          취소
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            flex: 2, padding: '10px', background: '#FF6F0F', color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
            cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? '저장 중...' : isNew ? '등록' : '수정 저장'}
        </button>
      </div>
    </Modal>
  );
}

// ───────────────────────── 공통 ─────────────────────────

function Modal({ children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, padding: 20,
          maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, color: '#374151', fontWeight: 600, marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}


// ── V20: 공급처 상세 미니 카드 (ParticipateModal 내부) ──
function SupplierDetailCard({ gb, suppliers }) {
  if (!gb?.supplierId) return null;
  const sup = (suppliers || []).find(s => s.id === gb.supplierId);
  if (!sup) {
    return gb.supplierName ? (
      <div style={{
        padding: 10, background: '#f9fafb', borderRadius: 8, marginBottom: 14,
        fontSize: 12, color: '#6b7280', border: '1px dashed #d1d5db',
      }}>
        🏬 공급처: {gb.supplierName} <span style={{ fontSize: 10 }}>(마스터에서 삭제됨)</span>
      </div>
    ) : null;
  }
  const statusColor = sup.status === 'active' ? '#FF6F0F' : sup.status === 'blocked' ? '#52525B' : '#6b7280';
  const statusLabel = sup.status === 'active' ? '활성' : sup.status === 'blocked' ? '거래중지' : '비활성';
  return (
    <div style={{
      padding: 12, background: '#FAFAFA', borderRadius: 8, marginBottom: 14,
      border: '1px solid #F4F4F5',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#9A3412' }}>🏬 {sup.name}</div>
          {(sup.contactName || sup.region) && (
            <div style={{ fontSize: 11, color: '#374151', marginTop: 2 }}>
              {sup.contactName}{sup.contactName && sup.region ? ' · ' : ''}{sup.region}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {sup.rating > 0 && (
            <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#F4F4F5', color: '#52525B', fontWeight: 700 }}>
              ★ {sup.rating.toFixed(1)}
            </span>
          )}
          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, color: statusColor, border: '1px solid ' + statusColor, fontWeight: 700 }}>
            {statusLabel}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 11, color: '#374151', marginTop: 6 }}>
        {sup.phone && <span>📞 {sup.phone}</span>}
        {sup.kakao && <span>💬 {sup.kakao}</span>}
        {sup.email && <span>✉ {sup.email}</span>}
      </div>
      {(sup.minOrder > 0 || sup.deliveryFee > 0 || sup.paymentTerms) && (
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, paddingTop: 6, borderTop: '1px dashed #E4E4E7' }}>
          {sup.minOrder > 0 && <span>최소발주 {formatKRW(sup.minOrder)}원 </span>}
          {sup.deliveryFee > 0 && <span>· 배송 {formatKRW(sup.deliveryFee)}원 </span>}
          {sup.paymentTerms && <span>· 결제: {sup.paymentTerms}</span>}
        </div>
      )}
      {sup.notes && (
        <div style={{ fontSize: 11, color: '#374151', marginTop: 6, padding: 6, background: '#fff', borderRadius: 4, fontStyle: 'italic' }}>
          📝 {sup.notes}
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
  borderRadius: 6, fontSize: 13, boxSizing: 'border-box',
};
